import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Space, Table, Tag, Typography } from 'antd';

import { api } from '../../api/client';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { API_ITEMS_URL } from '../../utils';
import { getAutomationRuns } from '../../services/automations';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { describeEntryResult, describeSkipped, describeTagChanges, describeTagRuleResult, elementOutcomeLines } from '../../utils/automationRunResults';
import { describeRunTrigger } from '../../utils/entryRules';
import useDateFormat from '../../hooks/useDateFormat';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';

/**
 * One rule's run ledger — the answer to "did it actually do anything, and what?".
 *
 * Paging is prev/next rather than antd's numbered pagination because the API
 * deliberately returns a page, not a count: the ledger is an append-only audit
 * table with a TTL on event runs, and a `countDocuments` per page-view would be
 * a scan nobody asked for. `rows.length === limit` is the honest signal for
 * "there is probably more", so that is what the Next button uses.
 *
 * Per-action results expand inline. Everything the engine records about a run
 * shows here — including the guard hits (`truncated`) that stopped it early,
 * because the plan's no-silent-caps rule ends at a human's eyes, not at the
 * database row.
 */

const { Text } = Typography;

const RUN_STATUS_COLORS = {
	succeeded: 'green',
	failed: 'red',
	skipped: 'default',
	pending: 'gold',
	unknown: 'default',
};

const TRUNCATION_REASONS = {
	writes: 'stopped at the 500-item write cap',
	timeBudget: 'ran out of time',
	depth: 'hit the cascade depth budget',
	notify: 'hit the notification cap',
	// A threshold sweep comparing one element to another cannot use an index,
	// so it is capped per rule per run — and says so rather than cutting.
	sweep: 'reached the sweep\'s per-run record cap',
};

// What a CLAMP did, in words. The summary row carries a COUNT (`clampedCount`)
// and a per-target row carries the REASON (`clamped`) — two different keys on
// purpose, because reusing one for both is exactly the bug that once rendered
// "unwritable already existed".
const CLAMP_WORDS = {
	min: 'held at the minimum',
	max: 'held at the maximum',
};

const PAGE_SIZE = 20;

// An id is not a title, but it is a handle (the AdminAttachments convention).
const shortId = id => `${ id }`.slice( 0, 8 ) + '…';

const resultsOf = run => ( Array.isArray( run?.results ) ? run.results : [] );

export default function AutomationRunsModal( { open, collectionId, ruleId, ruleName, page = 1, onPage, onClose } ) {

	const [ rows, setRows ] = useState( [] );
	const [ loading, setLoading ] = useState( false );
	const [ loadError, setLoadError ] = useState( false );
	// A read has answered for this rule; a page change then keeps the old page under the refresh cue.
	const [ loaded, setLoaded ] = useState( false );
	const [ attempt, setAttempt ] = useState( 0 );
	const loadedFor = useRef( null );
	const [ itemsById, setItemsById ] = useState( {} );

	useDateFormat();

	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById;
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	useEffect( () => {
		if ( ! open || ! collectionId || ! ruleId ) return undefined;
		let active = true;
		if ( loadedFor.current !== ruleId ) {
			loadedFor.current = ruleId;
			setRows( [] );
			setLoaded( false );
		}
		setLoading( true );
		setLoadError( false );
		getAutomationRuns( collectionId, ruleId, { limit: PAGE_SIZE, skip: ( page - 1 ) * PAGE_SIZE } )
			.then( data => { if ( active ) { setRows( Array.isArray( data ) ? data : [] ); setLoaded( true ); } } )
			.catch( () => { if ( active ) setLoadError( true ); } )
			.finally( () => { if ( active ) setLoading( false ); } );
		return () => { active = false; };
	}, [ open, collectionId, ruleId, page, attempt ] );

	// Every item this page's actions touched, in ONE batch call. Titles are a
	// convenience column, so an unreadable or deleted id simply keeps its
	// shortened id rather than costing a second request wave.
	const itemIds = useMemo( () => {
		const ids = new Set();
		rows.forEach( run => resultsOf( run ).forEach( result => {
			const id = normalizeId( result?.item_id );
			if ( id ) ids.add( id );
		} ) );
		return Array.from( ids );
	}, [ rows ] );

	useEffect( () => {
		if ( ! itemIds.length ) return undefined;
		let active = true;
		// Chunked at the batch endpoint's 100-id cap (it silently slices past
		// that) — a page of update-items runs can reference far more targets.
		const chunks = [];
		for ( let i = 0; i < itemIds.length; i += 100 ) chunks.push( itemIds.slice( i, i + 100 ) );
		Promise.all( chunks.map( chunk =>
			api.get( API_ITEMS_URL, { params: { ids: chunk.join( ',' ) } } )
				.then( ( { data } ) => data || [] )
				.catch( () => [] ) // degrade to raw ids
		) ).then( results => {
			if ( ! active ) return;
			const fetched = results.flat();
			if ( ! fetched.length ) return;
			setItemsById( prev => {
				const next = { ...prev };
				fetched.forEach( item => {
					const id = normalizeId( item?._id );
					if ( id ) next[ id ] = item;
				} );
				return next;
			} );
		} );
		return () => { active = false; };
	}, [ itemIds ] );

	const titleFor = itemId => {
		const item = itemsById[ itemId ];
		if ( ! item ) return shortId( itemId );
		const cardId = normalizeId( item?.card_id );
		return getItemDisplayValue( {
			card: cardsById?.[ cardId ],
			elementDefs: getElementsForCard ? getElementsForCard( cardId ) : [],
			item,
			fallback: 'Untitled item',
		} );
	};

	const tableRows = useMemo(
		() => rows.map( ( run, index ) => ( { key: normalizeId( run?._id ) || `run-${ index }`, run } ) ),
		[ rows ]
	);

	const columns = [
		{
			title: 'Status',
			dataIndex: 'run',
			key: 'status',
			render: run => {
				const status = run?.status || 'unknown';
				return <Tag color={RUN_STATUS_COLORS[ status ] || 'default'}>{status}</Tag>;
			},
		},
		{
			title: 'When',
			dataIndex: 'run',
			key: 'when',
			render: run => ( run?.startedAt ? formatDisplayDateTime( run.startedAt ) : <Text type='secondary'>—</Text> ),
		},
		{
			// What set it off: an item's event, or which entries of which group (KPR-687).
			title: 'Trigger',
			dataIndex: 'run',
			key: 'trigger',
			render: run => describeRunTrigger( run?.trigger ) || <Text type='secondary'>—</Text>,
		},
		{
			title: 'Duration',
			dataIndex: 'run',
			key: 'duration',
			render: run => ( run?.durationMs === undefined || run?.durationMs === null
				? <Text type='secondary'>—</Text>
				: `${ run.durationMs }ms` ),
		},
		{
			title: 'Items written',
			dataIndex: 'run',
			key: 'itemsWritten',
			render: run => resultsOf( run ).reduce( ( sum, result ) => sum + ( Number( result?.itemsWritten ) || 0 ), 0 ),
		},
	];

	const expandedRowRender = ( { run } ) => {
		const results = resultsOf( run );
		if ( ! results.length && ! run?.error ) return <Text type='secondary'>No actions ran.</Text>;
		return (
			<Space direction='vertical' size={4} style={{ width: '100%' }}>
				{run?.error ? <Text type='danger'>{run.error}</Text> : null}
				{results.map( ( result, index ) => {
					const itemId = normalizeId( result?.item_id );
					const skipped = describeSkipped( result?.skipped, result?.held );
					const tagChanges = describeTagChanges( result?.tags );
					const tagRule = describeTagRuleResult( result );
					const outcomes = elementOutcomeLines( result?.elements );
					const entryWords = describeEntryResult( result );
					return (
						<div key={index}>
							<Space size={8} wrap>
								<Text strong>{result?.action || 'action'}</Text>
								<Tag color={result?.ok ? 'green' : 'red'}>{result?.ok ? 'ok' : 'failed'}</Tag>
								{/* Expected-item vocabulary: without these, met-with-0
								    and unmet-with-7 render as the identical row. */}
								{result?.met !== undefined ? (
									<Tag color={result.met ? 'green' : 'gold'}>{result.met ? 'expectation met' : 'expectation unmet'}</Tag>
								) : null}
								{result?.count !== undefined ? <Text type='secondary'>{`${ result.count } found`}</Text> : null}
								{/* Fan-out and generation vocabulary: "3 written" alone
								    cannot tell a manager whether the other fifteen were
								    already there or never matched. */}
								{result?.matched !== undefined ? <Text type='secondary'>{`${ result.matched } matched`}</Text> : null}
								{result?.sources !== undefined ? <Text type='secondary'>{`${ result.sources } records`}</Text> : null}
								{result?.slots !== undefined ? <Text type='secondary'>{`${ result.slots } occurrences`}</Text> : null}
								{result?.created !== undefined ? <Text type='secondary'>{`${ result.created } created`}</Text> : null}
								{result?.slot ? <Tag>{`slot: ${ result.slot }`}</Tag> : null}
								{result?.suppressed ? <Tag>{`suppressed: ${ result.suppressed }`}</Tag> : null}
								{/* Threshold vocabulary: WHICH reading crossed WHICH bound.
								    A notify writes nothing, so without these the run row
								    would say an alert happened and nothing about why.
								    `valueText`/`boundText` are the same two readings as the
								    element renders them ("22.7 kg"); `value`/`bound` are the
								    canonical magnitudes the API compares on, and a
								    measurement's canonical base — 22.6796185, no unit — is
								    not something a person can read. Rows written before the
								    API sent the text fall back to them. */}
								{result?.bound !== undefined ? (
									<Text type='secondary'>
										{`${ result.element || 'value' } ${ result.valueText ?? result.value } vs ${ result.boundText ?? result.bound }`}
									</Text>
								) : null}
								{result?.crossed ? <Tag color='gold'>crossed</Tag> : null}
								{result?.renotified ? <Tag>still crossed</Tag> : null}
								{result?.fired !== undefined ? <Text type='secondary'>{`${ result.fired } fired`}</Text> : null}
								{/* Adjustment vocabulary: a clamp is a SUCCESS that did not
								    store the number the rule asked for, which is the one
								    outcome a green run row could otherwise hide. */}
								{result?.clampedCount ? (
									<Tag color='gold' title='The adjustment would have gone out of range on these records, so it stopped at the limit'>
										{`${ result.clampedCount } held at a limit`}
									</Tag>
								) : null}
								{result?.clamped ? (
									<Tag color='gold' title={`Asked for ${ result.requested }, stored ${ result.stored }`}>
										{`${ result.element || 'value' }: ${ CLAMP_WORDS[ result.clamped ] || result.clamped }`}
									</Tag>
								) : null}
								{result?.recipients !== undefined ? <Text type='secondary'>{`${ result.recipients } notified`}</Text> : null}
								{result?.emailed ? <Text type='secondary'>{`${ result.emailed } emailed`}</Text> : null}
								{result?.emailSuppressed ? <Tag color='gold'>{`${ result.emailSuppressed } emails suppressed`}</Tag> : null}
								{result?.itemsWritten ? <Text type='secondary'>{`${ result.itemsWritten } written`}</Text> : null}
								{/* Tags (T6a): an update-items action's Add tag / Remove tag,
								    and an automatic tag's own put-on / taken-off. */}
								{tagChanges ? <Text type='secondary' title={tagChanges.title} data-testid='run-tags'>{tagChanges.text}</Text> : null}
								{tagRule ? <Tag title={tagRule.title}>{tagRule.text}</Tag> : null}
								{entryWords ? <Text type='secondary' title={entryWords.title} data-testid='run-entries'>{entryWords.text}</Text> : null}
								{itemId ? <Text type='secondary'>{titleFor( itemId )}</Text> : null}
								{/* A refused element does not fail the action (filtering
								    unknown names per target is deliberate for an
								    open-ended scope) — but it must not pass unmentioned. */}
								{result?.partial ? (
									<Tag color='gold' title='Some elements were refused — see the breakdown below'>partial</Tag>
								) : null}
								{/* `skipped` is a COUNT on create-item's dedupe and a
								    REASON STRING everywhere else; one branch for both
								    printed "unwritable already existed". */}
								{skipped ? <Tag title={skipped.title}>{skipped.text}</Tag> : null}
								{result?.truncated ? (
									<Tag color='gold' title={TRUNCATION_REASONS[ result.truncated ] || result.truncated}>
										{`stopped early: ${ result.truncated }`}
									</Tag>
								) : null}
							</Space>
							{/* The line that would have ended a day-long investigation:
							    which elements the action wrote, and why it left the
							    rest alone. */}
							{outcomes.length ? (
								<div>
									<Space size={8} wrap>
										{outcomes.map( outcome => (
											<Text key={outcome.outcome} type='secondary' title={outcome.title}>{outcome.text}</Text>
										) )}
									</Space>
								</div>
							) : null}
							{result?.error ? <div><Text type='danger'>{result.error}</Text></div> : null}
							{( result?.warnings || [] ).map( ( warning, wi ) => (
								<div key={wi}><Text type='warning'>{`${ warning }`}</Text></div>
							) )}
						</div>
					);
				} )}
			</Space>
		);
	};

	// The server hands back a page, never a total — a full page is the only
	// evidence there might be another one.
	const hasMore = rows.length === PAGE_SIZE;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={720}
			destroyOnClose
			title={`Runs — ${ ruleName || 'rule' }`}
		>
			<DialogLoadState
				loaded={loaded}
				loading={loading}
				error={loadError}
				label='this run history'
				onRetry={() => setAttempt( n => n + 1 )}
				skeleton={<Skeleton variant='table-row' count={5} columns={5} />}
				minHeight={280}
			>
				{/* Five columns since KPR-687 added Trigger: on a phone the table
				    scrolls sideways inside the window, never the page. */}
				<Table
					size='small'
					columns={columns}
					dataSource={tableRows}
					pagination={false}
					expandable={{ expandedRowRender }}
					locale={{ emptyText: 'No runs yet.' }}
					scroll={{ x: 'max-content' }}
				/>
			</DialogLoadState>

			<Space style={{ marginTop: 12 }}>
				<Button size='small' disabled={page <= 1} onClick={() => onPage?.( page - 1 )}>Previous</Button>
				<Text type='secondary'>{`Page ${ page }`}</Text>
				<Button size='small' disabled={! hasMore} onClick={() => onPage?.( page + 1 )}>Next</Button>
			</Space>
		</Modal>
	);
}
